/**
 * Fishing Engine
 * Handles all fishing mechanics and resolution
 */

import {
  FishInstance,
  Quality,
  QualityMultipliers,
  FishRarity,
  FishingEventType,
  GameEvent,
  FishingState,
  awardXP,
  XP_BY_QUALITY,
  type FlyCastBonus,
} from '@/types';
import {
  getFishDefinition,
  getZoneDefinition,
  RarityWeights,
  getFlyPatternDefinition,
} from '@/data';
import { useGameStore } from '@/state';
import { eventBus } from '@/services/EventBus';
import { saveManager } from '@/systems/persistence/SaveManager';
import {
  weightedRandom,
  randomChance,
  calculateFishingTime,
  calculateCatchChance,
  rollFishLength,
  rollFishWeight,
  rollFishHealth,
  rollIsWild,
  applyWildLengthBonus,
  applyWildWeightBonus,
  calculateFishQuality,
  rollFlyFishSpecies,
  shouldConsumeFly,
} from '@/utils';
import { equipmentManager } from '../equipment/EquipmentManager';
import { fishingEventSystem } from './FishingEventSystem';

export class FishingEngine {
  /** One-shot catch chance bonus (0-1) applied on next completeFishing, then cleared */
  private pendingCatchBonus: number = 0;

  /** Pending fly cast bonus (species targeting + quality), consumed in processCatchResult */
  private pendingFlyCastBonus: FlyCastBonus | null = null;

  /**
   * Apply a one-shot catch chance bonus for the current cast.
   * Used by the sweet spot timing mechanic in FishButton.
   * The bonus is consumed when the catch resolves.
   */
  applySweetSpotBonus(bonus: number): void {
    this.pendingCatchBonus = bonus;
  }

  /**
   * Apply fly cast minigame result bonuses.
   * Sets pending catch + quality + species targeting bonuses.
   * Called by FlyCastOverlay when the player completes a trace.
   */
  applyFlyCastBonus(bonus: FlyCastBonus): void {
    this.pendingFlyCastBonus = bonus;
    // Reuse existing catch bonus mechanism for catch chance portion
    this.pendingCatchBonus = bonus.catchBonus;
  }

  /**
   * Instantly resolve the current cast (skip remaining timer).
   * Used by debug/instant-fish features.
   */
  resolveInstantly(): void {
    const state = useGameStore.getState();
    if (state.fishingState.status === FishingState.WAITING) {
      this.completeFishing();
    }
  }

  /**
   * Resolve a fly cast immediately — no fishing timer.
   * The fly cast minigame IS the entire fishing action for fly rods.
   * Called by FlyCastOverlay when the trace completes.
   */
  resolveFlyCast(bonus: FlyCastBonus): void {
    const state = useGameStore.getState();

    // Check inventory before proceeding
    const inventory = state.playerState.inventory;
    if (inventory.currentCount >= inventory.capacity) {
      console.warn('❌ Cannot resolve fly cast - inventory is full!');
      eventBus.emit(GameEvent.INVENTORY_FULL, {});
      return;
    }

    // Set bonuses (consumed by completeFishing → processCatchResult)
    this.pendingFlyCastBonus = bonus;
    this.pendingCatchBonus = bonus.catchBonus;

    // Track the cast
    state.incrementStat('totalClicks');

    // Emit fishing started for event tracking
    eventBus.emit(GameEvent.FISHING_STARTED, {
      attempt: { duration: 0, zone: state.playerState.currentZone },
    });

    // Resolve immediately — completeFishing handles catch roll, inventory, events, save
    this.completeFishing();
  }

  /**
   * Reduce remaining fishing time by a percentage.
   * Used by sweet spot Perfect timing: cuts remaining time in half.
   * @param factor - Fraction to keep (e.g. 0.5 = keep half the remaining time)
   */
  reduceRemainingTime(factor: number): void {
    const state = useGameStore.getState();
    if (state.fishingState.status !== FishingState.WAITING) return;

    const newTimeRemaining = state.fishingState.timeRemaining * factor;
    state.setFishingState({
      timeRemaining: Math.max(0.05, newTimeRemaining),
    });
  }

  /**
   * Start a fishing attempt
   */
  startFishing(): void {
    const state = useGameStore.getState();

    // Check if already fishing
    if (state.fishingState.status !== FishingState.IDLE) {
      console.warn('Already fishing!');
      return;
    }

    // Preserve fly cast bonus if it was set before startFishing() (fly rod flow:
    // minigame → applyFlyCastBonus → startFishing). Only reset sweet spot bonus
    // for conventional rods. The fly cast catch bonus is carried through.
    if (!this.pendingFlyCastBonus) {
      this.pendingCatchBonus = 0;
    }

    // Check if inventory is full before starting
    const inventory = state.playerState.inventory;
    if (inventory.currentCount >= inventory.capacity) {
      console.warn('❌ Cannot start fishing - inventory is full!');
      eventBus.emit(GameEvent.INVENTORY_FULL, {});
      return;
    }

    // Get current zone and stats
    const zone = getZoneDefinition(state.playerState.currentZone);
    const playerStats = state.playerStats;

    // Calculate fishing duration (Frenzy reduces to 1.5s)
    let duration = calculateFishingTime(zone.baseFishingTime, playerStats);
    if (fishingEventSystem.hasActiveEffect(FishingEventType.FEEDING_FRENZY)) {
      duration = Math.min(duration, 1.5);
    }

    // Update fishing state
    state.setFishingState({
      status: FishingState.WAITING,
      timeRemaining: duration,
      startedAt: Date.now(),
    });

    // Emit event
    eventBus.emit(GameEvent.FISHING_STARTED, {
      attempt: { duration, zone: zone.id },
    });

    // Increment click stat
    state.incrementStat('totalClicks');
  }

  /**
   * Update fishing timer (called from game loop)
   */
  update(deltaTime: number): void {
    const state = useGameStore.getState();
    const fishingState = state.fishingState;

    if (fishingState.status !== FishingState.WAITING) {
      return;
    }

    const newTimeRemaining = fishingState.timeRemaining - deltaTime;

    if (newTimeRemaining <= 0) {
      // Fishing complete - resolve catch
      this.completeFishing();
    } else {
      // Update remaining time
      state.setFishingState({
        timeRemaining: newTimeRemaining,
      });
    }
  }

  /**
   * Complete fishing and roll for catch
   */
  private completeFishing(): void {
    const state = useGameStore.getState();

    // Get current zone and stats
    const zone = getZoneDefinition(state.playerState.currentZone);
    const playerStats = state.playerStats;

    // Roll for a random fishing event
    const fishingEvent = fishingEventSystem.rollEvent();

    // Broken Line: skip the catch entirely
    if (fishingEvent?.type === FishingEventType.BROKEN_LINE) {
      console.log('💥 Broken Line! Fish lost, but consolation buff applied.');

      // Reset fishing state
      state.setFishingState({
        status: FishingState.IDLE,
        timeRemaining: 0,
        startedAt: null,
      });

      eventBus.emit(GameEvent.FISHING_COMPLETED, { success: false });
      eventBus.emit(GameEvent.FISH_LOST, {});
      return;
    }

    // Calculate catch chance with active effects
    let catchChance = calculateCatchChance(zone.baseCatchChance, playerStats);

    // Sweet spot timing bonus (consumed once per cast)
    if (this.pendingCatchBonus > 0) {
      catchChance = Math.min(1.0, catchChance + this.pendingCatchBonus);
      this.pendingCatchBonus = 0;
    }

    // Frenzy: 95% catch chance
    if (fishingEventSystem.hasActiveEffect(FishingEventType.FEEDING_FRENZY)) {
      catchChance = Math.max(catchChance, 0.95);
      fishingEventSystem.consumeEffect(FishingEventType.FEEDING_FRENZY);
    }

    // Consolation from Broken Line: +20% catch chance
    if (fishingEventSystem.hasActiveEffect(FishingEventType.BROKEN_LINE)) {
      catchChance = Math.min(1.0, catchChance + 0.20);
      fishingEventSystem.consumeEffect(FishingEventType.BROKEN_LINE);
    }

    // Roll for success
    const success = randomChance(catchChance);

    // Process the catch result with event modifiers
    this.processCatchResult(success, fishingEvent ?? undefined);

    // Double Hook: catch a second fish if the first succeeded
    if (success && fishingEvent?.type === FishingEventType.DOUBLE_HOOK) {
      console.log('🎣 Double Hook! Catching a second fish...');
      this.processCatchResult(true);
    }

    // Reset fishing state
    state.setFishingState({
      status: FishingState.IDLE,
      timeRemaining: 0,
      startedAt: null,
    });

    // Emit completion event
    eventBus.emit(GameEvent.FISHING_COMPLETED, { success });
  }

  /**
   * Instant fish (for debug)
   * Uses same logic as normal fishing but skips timer
   */
  instantFish(): void {
    const state = useGameStore.getState();

    // Get current zone and stats
    const zone = getZoneDefinition(state.playerState.currentZone);
    const playerStats = state.playerStats;

    // Roll for success
    const catchChance = calculateCatchChance(zone.baseCatchChance, playerStats);
    const success = randomChance(catchChance);

    // Process the catch result (same as normal fishing)
    this.processCatchResult(success);
  }

  /**
   * Process catch result (shared logic for normal and instant fishing)
   * @param success - Whether the catch roll succeeded
   * @param fishingEvent - Optional fishing event modifying this catch
   */
  private processCatchResult(success: boolean, fishingEvent?: { type: FishingEventType }): void {
    const state = useGameStore.getState();

    if (success) {
      // Get current zone and stats
      const zone = getZoneDefinition(state.playerState.currentZone);

      // Roll fish type — fly cast overrides species roll with pattern targeting
      let fishId: string;
      if (this.pendingFlyCastBonus) {
        const flyPattern = this.pendingFlyCastBonus.flyPatternId
          ? getFlyPatternDefinition(this.pendingFlyCastBonus.flyPatternId) ?? null
          : null;
        // Build minimal fish definitions lookup for the species roller
        const fishDefs: Record<string, { rarity: FishRarity }> = {};
        for (const id of zone.fishPool) {
          fishDefs[id] = getFishDefinition(id);
        }
        fishId = rollFlyFishSpecies(
          zone.fishPool,
          fishDefs,
          flyPattern,
          this.pendingFlyCastBonus.castTier,
        );
      } else if (fishingEvent?.type === FishingEventType.RARE_SIGHTING) {
        fishId = this.rollRareSpecies(zone.fishPool);
      } else {
        fishId = this.rollFishSpecies(zone.fishPool);
      }
      const definition = getFishDefinition(fishId);

      // Determine if wild or hatchery
      const isWild = rollIsWild(definition.canBeHatchery);

      // Generate base fish stats
      let length = rollFishLength(definition.lengthRange[0], definition.lengthRange[1]);
      let weight = rollFishWeight(length, definition.lengthRange, definition.weightRange);

      // Apply wild bonuses (5% boost to length and weight for wild fish)
      if (isWild && definition.canBeHatchery) {
        length = applyWildLengthBonus(length, isWild);
        weight = applyWildWeightBonus(weight, isWild);
      }

      const health = rollFishHealth(isWild);

      // Calculate quality based on stats (apply player's quality bonus from equipment + progression + fly cast)
      const playerStats = state.playerStats;
      let effectiveQualityBonus = playerStats.qualityBonus;
      if (this.pendingFlyCastBonus) {
        effectiveQualityBonus += this.pendingFlyCastBonus.qualityBonus;
      }
      let quality = calculateFishQuality(
        length,
        weight,
        health,
        definition.lengthRange,
        definition.weightRange,
        definition.rarity,
        effectiveQualityBonus
      );

      // NOTE: pendingFlyCastBonus is cleared after bait consumption below

      // Big One: floor quality at RARE
      if (fishingEvent?.type === FishingEventType.BIG_ONE) {
        const qualityTiers = [
          Quality.COMMON,
          Quality.UNCOMMON,
          Quality.RARE,
          Quality.EPIC,
          Quality.LEGENDARY,
          Quality.UNIQUE,
        ];
        const currentIndex = qualityTiers.indexOf(quality);
        const rareIndex = qualityTiers.indexOf(Quality.RARE);
        if (currentIndex < rareIndex) {
          quality = Quality.RARE;
        }
      }

      // Create fish instance
      const fish: FishInstance = {
        definitionId: fishId,
        quality: quality,
        caughtAt: Date.now(),
        length: length,
        weight: weight,
        health: health,
        isWild: isWild,
      };

      // Try to add to inventory
      console.log('🐟 Attempting to add fish to inventory:', fish);
      const added = state.addFishToInventory(fish);
      console.log('📦 Fish added to inventory:', added);

      if (added) {
        // Consume bait on successful catch.
        // Fly patterns use conditional consumption: good casts preserve the fly,
        // bad casts (<60% accuracy) have a 50% chance to lose it.
        if (this.pendingFlyCastBonus) {
          if (shouldConsumeFly(this.pendingFlyCastBonus.accuracy)) {
            equipmentManager.consumeBait('player');
            console.log('🪶 Fly lost on poor cast');
          } else {
            console.log('🪶 Fly preserved — good cast!');
          }
        } else {
          // Conventional bait: always consumed
          equipmentManager.consumeBait('player');
        }

        // Clear fly cast bonus now that it's been fully consumed
        this.pendingFlyCastBonus = null;

        // Lucky Catch: award bonus gold immediately (50% of quality-adjusted fish value)
        if (fishingEvent?.type === FishingEventType.LUCKY_CATCH) {
          const qualityMultiplier = QualityMultipliers[fish.quality] ?? 1.0;
          const luckyBonus = Math.round(definition.baseValue * qualityMultiplier * 0.5);
          useGameStore.getState().addCurrency(luckyBonus);
          console.log(`💰 Lucky Catch! Bonus +${luckyBonus}g`);
        }

        // Success - emit event
        console.log('✅ Fish caught successfully:', definition.name, fish.quality);

        // Track discovery (must happen before event emission for UI consistency)
        state.trackFishDiscovery(fish);

        eventBus.emit(GameEvent.FISH_CAUGHT, {
          fish,
          definition,
        });

        // Update stats (use draft mutations to avoid race conditions)
        state.incrementStat('totalFishCaught');

        // Update species stats using immer draft
        const currentSpeciesCount = state.playerState.stats.fishCaughtBySpecies[fishId] || 0;
        useGameStore.setState((draft) => {
          draft.playerState.stats.fishCaughtBySpecies[fishId] = currentSpeciesCount + 1;
        });

        // Update quality stats using immer draft
        const qualityKey = quality as keyof typeof state.playerState.stats.fishCaughtByQuality;
        if (qualityKey in state.playerState.stats.fishCaughtByQuality) {
          useGameStore.setState((draft) => {
            draft.playerState.stats.fishCaughtByQuality[qualityKey]++;
          });
        }

        // Award XP to player
        const xpGained = XP_BY_QUALITY[quality] || 1;
        const oldLevel = useGameStore.getState().playerState.progression.progress.level;

        useGameStore.setState((draft) => {
          const newProgression = awardXP(draft.playerState.progression, xpGained);
          draft.playerState.progression = newProgression;
        });

        // Check for level-up and log
        const newLevel = useGameStore.getState().playerState.progression.progress.level;
        if (newLevel > oldLevel) {
          console.log(
            `🎉 LEVEL UP! Player reached level ${newLevel}! ` +
            `Stat points available: ${useGameStore.getState().playerState.progression.progress.unspentStatPoints}`
          );
          eventBus.emit(GameEvent.PLAYER_LEVEL_UP, { newLevel, oldLevel });

          // Recalculate stats when leveling up (in case player has auto-allocated stats in future)
          equipmentManager.recalculateStats();
        }

        // Save to localStorage after successful catch
        saveManager.save();
      } else {
        // Inventory full
        console.warn('❌ Inventory is full!');
        eventBus.emit(GameEvent.INVENTORY_FULL, {});
      }
    } else {
      // Failed to catch
      console.log('💨 Fish got away!');
      eventBus.emit(GameEvent.FISH_LOST, {});
    }
  }

  /**
   * Roll a rare+ species from the fish pool (for Rare Sighting event)
   */
  private rollRareSpecies(fishPool: string[]): string {
    const rarePool = fishPool.filter((fishId) => {
      const definition = getFishDefinition(fishId);
      return definition.rarity === FishRarity.RARE || definition.rarity === FishRarity.EPIC || definition.rarity === FishRarity.LEGENDARY;
    });

    // If no rare fish in pool, fall back to normal roll
    if (rarePool.length === 0) {
      return this.rollFishSpecies(fishPool);
    }

    // Equal weight among rare+ fish
    const items = rarePool.map((fishId) => ({
      id: fishId,
      weight: 1,
    }));

    return weightedRandom(items);
  }

  /**
   * Attempt to catch a fish (for buddies or other automated systems)
   * @param options - Fishing context options
   * @returns FishInstance if caught, null if missed
   */
  attemptCatch(options?: { isBuddy?: boolean; buddyModifiers?: any }): FishInstance | null {
    const state = useGameStore.getState();
    const zone = getZoneDefinition(state.playerState.currentZone);
    const playerStats = state.playerStats;

    // Calculate catch chance (with buddy modifiers if applicable)
    let catchChance = calculateCatchChance(zone.baseCatchChance, playerStats);
    if (options?.isBuddy && options.buddyModifiers) {
      catchChance -= options.buddyModifiers.catchReduction;
    }

    // Roll for success
    const success = randomChance(catchChance);

    if (!success) {
      return null; // Catch failed
    }

    // Roll fish type
    const fishId = this.rollFishSpecies(zone.fishPool);
    const definition = getFishDefinition(fishId);

    // Determine if wild or hatchery
    const isWild = rollIsWild(definition.canBeHatchery);

    // Generate base fish stats
    let length = rollFishLength(definition.lengthRange[0], definition.lengthRange[1]);
    let weight = rollFishWeight(length, definition.lengthRange, definition.weightRange);

    // Apply wild bonuses (5% boost to length and weight for wild fish)
    if (isWild && definition.canBeHatchery) {
      length = applyWildLengthBonus(length, isWild);
      weight = applyWildWeightBonus(weight, isWild);
    }

    const health = rollFishHealth(isWild);

    // Calculate quality based on stats (apply buddy's quality bonus if available)
    let quality = calculateFishQuality(
      length,
      weight,
      health,
      definition.lengthRange,
      definition.weightRange,
      definition.rarity,
      options?.buddyModifiers?.qualityBonus ?? 0
    );

    // Apply buddy quality cap if applicable (buddy caught fish are lower quality)
    if (options?.isBuddy && options.buddyModifiers?.qualityCap) {
      const qualityCap = options.buddyModifiers.qualityCap;

      // Define quality tier hierarchy
      const qualityTiers = [
        Quality.COMMON,
        Quality.UNCOMMON,
        Quality.RARE,
        Quality.EPIC,
        Quality.LEGENDARY,
        Quality.UNIQUE,
      ];

      // Cap mapping: if cap is 'uncommon', don't allow rare or better
      const capMap: Record<string, Quality> = {
        'common': Quality.COMMON,
        'uncommon': Quality.UNCOMMON,
        'rare': Quality.RARE,
        'epic': Quality.EPIC,
      };

      const maxAllowedQuality = capMap[qualityCap];
      if (maxAllowedQuality) {
        const currentIndex = qualityTiers.indexOf(quality);
        const maxIndex = qualityTiers.indexOf(maxAllowedQuality);

        // Downgrade if quality exceeds cap
        if (currentIndex > maxIndex) {
          quality = maxAllowedQuality;
        }
      }
    }

    // Create fish instance
    const fish: FishInstance = {
      definitionId: fishId,
      quality: quality,
      caughtAt: Date.now(),
      length: length,
      weight: weight,
      health: health,
      isWild: isWild,
    };

    return fish;
  }

  /**
   * Roll fish species from pool
   */
  private rollFishSpecies(fishPool: string[]): string {
    // Get rarity weights for each fish
    const fishWithWeights = fishPool.map((fishId) => {
      const definition = getFishDefinition(fishId);
      return {
        id: fishId,
        weight: RarityWeights[definition.rarity],
      };
    });

    return weightedRandom(fishWithWeights);
  }

}

// Singleton instance
export const fishingEngine = new FishingEngine();
